import axios from "axios";
import { isSecondaryLevel } from "../data/academicLevels";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import swal from "sweetalert";
import { BASE_URL } from "../App";
import "../styles/registeruser.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faCheckCircle, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";



// 1. تنظيف المدخلات (Sanitization)
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    // إزالة الأكواد المشبوهة
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
    .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '')
    .replace(/<form\b[^<]*(?:(?!<\/form>)<[^<]*)*<\/form>/gi, '')
    // إزالة الأحداث الخطيرة
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/vbscript:/gi, '')
    .replace(/data:/gi, '')
    // إزالة وسوم HTML خطيرة
    .replace(/<[^>]*>/g, '')
    // تنظيف الرموز الخاصة
    .replace(/[<>'"&]/g, (match) => {
      const entityMap = {
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '&': '&amp;'
      };
      return entityMap[match];
    });
};

// 2. التحقق من نوع البيانات (Validation)
const validateDataType = (input, fieldName) => {
  if (typeof input !== 'string') return false;
  
  // فحص أطوال معقولة
  const maxLengths = {
    fname1: 50,
    fname2: 50,
    lname1: 50,
    lname2: 50,
    phone: 15,
    pphone: 15,
    email: 100,
    password: 100,
    center_id: 10,
    government: 50
  };
  
  if (maxLengths[fieldName] && input.length > maxLengths[fieldName]) {
    return false;
  }
  
  // فحص الأنماط المشبوهة
  const suspiciousPatterns = [
    /<[^>]*>/,           // HTML tags
    /javascript:/i,      // JavaScript protocol
    /vbscript:/i,        // VBScript protocol
    /on\w+\s*=/i,        // Event handlers
    /expression\s*\(/i,  // CSS expressions
    /url\s*\(/i,         // CSS url()
    /import\s+/i,        // CSS @import
    /\s*=\s*eval\s*\(/i, // eval() calls
    /\s*=\s*new\s+/i,    // new Object()
    /\.\./,              // Path traversal
    /union\s+select/i,   // SQL injection
    /drop\s+table/i,     // SQL injection
    /insert\s+into/i,    // SQL injection
    /delete\s+from/i     // SQL injection
  ];
  
  return !suspiciousPatterns.some(pattern => pattern.test(input));
};

// 3. فحص أكواد HTML وJS (Anti-XSS)
const detectXSS = (input) => {
  if (typeof input !== 'string') return false;
  
  const xssPatterns = [
    // أكواد JavaScript خطيرة
    /alert\s*\(/i,
    /confirm\s*\(/i,
    /prompt\s*\(/i,
    /document\./i,
    /window\./i,
    /location\./i,
    /eval\s*\(/i,
    /function\s*\(/i,
    /var\s+\w+/i,
    /let\s+\w+/i,
    /const\s+\w+/i,
    
    // وسوم HTML خطيرة
    /<script/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /<form/i,
    /<input/i,
    /<textarea/i,
    /<select/i,
    /<button/i,
    /<link/i,
    /<meta/i,
    /<style/i,
    
    // بروتوكولات خطيرة
    /javascript:/i,
    /vbscript:/i,
    /data:text\/html/i,
    /data:application/i,
    
    // أحداث خطيرة
    /onload/i,
    /onerror/i,
    /onclick/i,
    /onmouseover/i,
    /onfocus/i,
    /onblur/i,
    /onchange/i,
    /onsubmit/i
  ];
  
  return xssPatterns.some(pattern => pattern.test(input));
};

// 4. دالة التنظيف الشاملة
const secureInput = (input, fieldName) => {
  if (!input || typeof input !== 'string') return input;
  
  // فحص XSS أولاً
  if (detectXSS(input)) {
    console.warn(`XSS attempt detected in field: ${fieldName}`);
    return '';
  }
  
  // التحقق من نوع البيانات
  if (!validateDataType(input, fieldName)) {
    console.warn(`Invalid data type in field: ${fieldName}`);
    return '';
  }
  
  // تنظيف المدخلات
  return sanitizeInput(input.trim());
};

// 5. فحص أساسي مبسط للملفات (حماية من XSS فقط)
// ملاحظة: تم تبسيط الفحص ليقبل الصور العادية مع الحفاظ على الحماية الأساسية
const validateBasicFile = async (file) => {
  try {
    if (!file) return false;
    
    // فحص أساسي للحجم
    if (file.size < 1024 || file.size > 5 * 1024 * 1024) return false;
    
    // فحص امتداد الملف
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    if (!hasValidExtension) return false;
    
    // فحص MIME type
    const allowedMimeTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedMimeTypes.includes(file.type)) return false;
    
    // فحص اسم الملف للحماية من XSS (أساسي فقط)
    if (detectXSS(fileName)) return false;
    
    // فحص أساسي لمحتوى الملف - البحث عن أكواد ضارة واضحة فقط
    const buffer = await file.arrayBuffer();
    const fileContent = new TextDecoder('utf-8', { fatal: false }).decode(new Uint8Array(buffer.slice(0, 1000)));
    
    // البحث عن أكواد خطيرة واضحة فقط
    const obviousMaliciousPatterns = [
      /<script/i,
      /javascript:/i,
      /<\?php/i,
      /<%/i,
      /eval\s*\(/i
    ];
    
    const hasMaliciousContent = obviousMaliciousPatterns.some(pattern => pattern.test(fileContent));
    return !hasMaliciousContent;
    
  } catch (error) {
    console.warn('Error validating file:', error);
    return false;
  }
};



const initialState = {
  email: "",
  password: "",
  fname1: "",
  fname2: "",
  lname1: "",
  lname2: "",
  pphone: "",
  phone: "",
  level: "",
  government: "",
  center_id: "",
  center: "",
  national_number: "",
  type: "",
  password_confirmation: "",
  student_kind : "",
  group_id: "",
  image: null
};

// Validation functions - Simplified
const validateField = (name, value, formData = {}) => {
  const errors = {};

  switch (name) {
    case 'fname1':
    case 'fname2':
    case 'lname1':
    case 'lname2':
      if (!value.trim()) {
        errors[name] = 'هذا الحقل مطلوب';
      }
      break;

    case 'phone':
    case 'pphone':
      if (!value.trim()) {
        errors[name] = 'رقم الهاتف مطلوب';
      } else if (value.trim().length !== 11) {
        errors[name] = 'رقم الهاتف يجب أن يكون 11 رقم';
      }
      break;

    case 'email':
      if (!value.trim()) {
        errors[name] = 'البريد الإلكتروني مطلوب';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
        errors[name] = 'يجب إدخال بريد إلكتروني صحيح';
      }
      break;

    case 'level':
      if (!value) {
        errors[name] = 'يجب اختيار السنة الدراسية';
      }
      break;

    case 'type':
      if (isSecondaryLevel(formData.level) && !value) {
        errors[name] = 'يجب اختيار نظام الدراسة';
      }
      break;

    case 'student_kind':
      if (!value) {
        errors[name] = 'يجب اختيار نوع الدراسة';
      } else if (formData.level === "1" && !["عام", "أزهر", "بكالوريا"].includes(value)) {
        errors[name] = 'نوع الدراسة غير صالح';
      } else if (formData.level !== "1" && !["عام", "أزهر"].includes(value)) {
        errors[name] = 'نوع الدراسة غير صالح';
      }
      break;

    case 'center':
      if (isSecondaryLevel(formData.level) && formData.type === '0' && !value) {
        errors[name] = 'يجب اختيار السنتر';
      }
      break;

    case 'center_id':
      if (isSecondaryLevel(formData.level) && formData.type === '0' && !value.trim()) {
        errors[name] = '  id المستر مطلوب ';
      }
      break;

    case 'government':
      if (!value) {
        errors[name] = 'يجب اختيار المحافظة';
      }
      break;

    case 'image':
      if (!value) {
        errors[name] = 'صورة الطالب مطلوبة';
      }
      break;

    case 'password':
      if (!value) {
        errors[name] = 'كلمة المرور مطلوبة';
      }
      break;

    case 'password_confirmation':
      if (!value) {
        errors[name] = 'تأكيد كلمة المرور مطلوب';
      } else if (value !== formData.password) {
        errors[name] = 'كلمة المرور غير متطابقة';
      }
      break;



    default:
      break;
  }

  return errors;
};

const validateForm = (formData) => {
  const errors = {};
  
  // Validate all fields
  Object.keys(formData).forEach(field => {
    const fieldErrors = validateField(field, formData[field], formData);
    if (fieldErrors[field]) {
      errors[field] = fieldErrors[field];
    }
  });



  return errors;
};

function Registeruser({ style }) {
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [academicYearSelected, setAcademicYearSelected] = useState(true);
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  
    swal({
      title: "⚠️ تعليمات هامة",
      text: "• المنصة تعمل على جميع الأجهزة (موبايل ولابتوب).\n" +
            "• لا يمكن تعديل البيانات إلا بالعودة للدعم .\n" +
            "• لا يمكن استرجاع أو تبديل الكورس بعد الاشتراك.\n" +
            "• لو فيه حاجه مش متأكد منها اسأل الدعم قبل الأشتراك\n" +
            "• استخدم واي فاي قوي والنت يكون مستقر جدا حتي لا تواجه مشكلة مع الفيديوهات\n" +
            "• الحساب مخصص للاستخدام الشخصي فقط ومشاركته تعرضه للإغلاق.\n\n" +
            "برجاء الالتزام بهذه التعليمات لضمان أفضل تجربة.",
      icon: "warning",
      button: {
        text: "حسناً، فهمت",
        className: "swal-ok-button",
      },
      className: "swal-instructions-custom",
    });

    return () => {
      swal.close(); 
    };
  }, []);

  // جلب المجموعات عند تحميل الصفحة إذا كان المستخدم قد اختار مستوى دراسي
  useEffect(() => {
    if (formData.level && isSecondaryLevel(formData.level)) {
      fetchGroups();
    }
  }, [formData.level]);

  const handleInputChange = async (event) => {
    const { name, value } = event.target;
    
    // تطبيق الحماية من XSS على المدخلات النصية
    const securedValue = secureInput(value, name);
    
    // إذا تم حذف المحتوى بسبب XSS، أظهر تحذير
    if (value && !securedValue && value.trim() !== '') {
      swal("تحذير أمني!", "تم رفض المدخلات لاحتوائها على محتوى غير آمن", "warning");
      return;
    }
    
    setFormData((prevData) => ({ ...prevData, [name]: securedValue }));

    // Handle level change
    if (name === "level") {
      if (isSecondaryLevel(value)) {
        setAcademicYearSelected(true);
        setFormData((prevData) => ({
          ...prevData,
          level: value,
          type: "",
          group_id: "",
          center: "",
          center_id: "",
          student_kind:
            value !== "1" && prevData.student_kind === "بكالوريا" ? "" : prevData.student_kind,
        }));
      } else {
        setAcademicYearSelected(false);
        setFormData((prevData) => ({ 
          ...prevData, 
          level: value,
          type: "",
          group_id: ""
        }));
      }
    }

    // Handle type change (center or online)
    if (name === "type") {
      if (value === "1") {
        // أونلاين - تعيين group_id = 1
        setFormData((prevData) => ({ 
          ...prevData, 
          type: "1",
          group_id: "1", // أونلاين group_id = 1
          center: "", // Clear center
          center_id: "" // Clear center_id
        }));
      } else if (value === "0") {
        // سنتر - جلب بيانات المجموعات
        setFormData((prevData) => ({ 
          ...prevData, 
          type: "0",
          group_id: "", // Clear group_id when switching to center
          center: "", // Clear center
          center_id: "" // Clear center_id
        }));
        fetchGroups(); // جلب بيانات المجموعات عند اختيار سنتر
      }
    }

    // Handle center change - set group_id based on selected center
    if (name === "center") {
      const selectedGroup = groups.find(group => group.name === value);
      if (selectedGroup) {
        setFormData((prevData) => ({ 
          ...prevData, 
          center: value,
          group_id: selectedGroup.id
        }));
      }
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    setTouched((prev) => ({ ...prev, image: true }));

    if (!file) {
      setFormData((prev) => ({ ...prev, image: null }));
      setImagePreview(null);
      setErrors((prev) => ({ ...prev, image: 'صورة الطالب مطلوبة' }));
      return;
    }

    const isValid = await validateBasicFile(file);
    if (!isValid) {
      swal(
        "خطأ!",
        "الصورة غير صالحة. يجب أن تكون JPG أو PNG أو WEBP وحجمها بين 1KB و 5MB",
        "warning"
      );
      event.target.value = "";
      setFormData((prev) => ({ ...prev, image: null }));
      setImagePreview(null);
      setErrors((prev) => ({ ...prev, image: 'صورة غير صالحة' }));
      return;
    }

    setFormData((prev) => ({ ...prev, image: file }));
    setImagePreview(URL.createObjectURL(file));
    setErrors((prev) => ({ ...prev, image: null }));
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    // Validate field on blur
    const fieldErrors = validateField(name, value, formData);
    if (fieldErrors[name]) {
      setErrors(prev => ({ ...prev, [name]: fieldErrors[name] }));
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    
    // Mark all fields as touched first
    setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
    
    // فحص أمني شامل قبل الإرسال
    const securityCheck = () => {
      let hasSecurityIssue = false;
      const textFields = ['fname1', 'fname2', 'lname1', 'lname2', 'phone', 'pphone', 'email', 'password', 'password_confirmation', 'center_id'];
      
      textFields.forEach(field => {
        const value = formData[field];
        if (value && typeof value === 'string') {
          if (detectXSS(value)) {
            console.error(`XSS detected in ${field}:`, value);
            hasSecurityIssue = true;
          }
          if (!validateDataType(value, field)) {
            console.error(`Invalid data type in ${field}:`, value);
            hasSecurityIssue = true;
          }
        }
      });
      
      return !hasSecurityIssue;
    };
    
    // تشغيل فحص الأمان
    if (!securityCheck()) {
      swal({
        title: "خطأ أمني!",
        text: "تم اكتشاف محتوى غير آمن في البيانات المدخلة. يرجى مراجعة البيانات.",
        icon: "error",
        button: "حسناً",
      });
      return;
    }
    
    // Validate entire form
    const formErrors = validateForm(formData);
    
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      
      swal({
        title: "خطأ في البيانات!",
        text: "يرجى مراجعة البيانات المدخلة وإصلاح الأخطاء المذكورة.",
        icon: "error",
        button: "حسناً",
      });
      
      // Scroll to first error
      const firstErrorField = document.querySelector('.input-field.has-error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      return;
    }

    setLoading(true);
    setErrors({});
    
    try {
      const formDataObj = new FormData();
      
      // تطبيق طبقة حماية إضافية قبل الإرسال
      Object.keys(formData).forEach((key) => {
        let value = formData[key];

        if (key === "image") {
          if (value instanceof File) {
            formDataObj.append("image", value);
          }
          return;
        }

        // تنظيف البيانات النصية مرة أخرى قبل الإرسال
        if (typeof value === "string" && key !== "national_id") {
          value = secureInput(value, key);
        }

        formDataObj.append(key, value ?? "");
      });
      
      const response = await axios.post(
        `${BASE_URL}/api/register`,
        formDataObj,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      
      const userId = response.data.data.user.id;
      setFormData(initialState);
      setImagePreview(null);
      
      swal({
        title: "تم تسجيل حسابك بنجاح! 🎉",
        text: `ID الخاص بك: ${userId}`,
        icon: "success",
        button: "حسناّ!",
      }).then(() => navigate("/loginuser"));
      
    } catch (e) {
      if (e.response && e.response.status === 422) {
        setErrors(e.response.data.errors);
        setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
        
        swal({
          title: "خطأ في البيانات!",
          text: "يرجى مراجعة البيانات المدخلة وإصلاح الأخطاء المذكورة.",
          icon: "error",
          button: "حسناً",
        });
      } else if (e.response && e.response.status === 429) {
        swal({
          title: `${e.response.data.message}`,
          // text: "يرجى المحاولة مرة أخرى أو التواصل مع الدعم الفني.",
          icon: "warning",
          button: "حسناً",
        });
      } else {
        swal({
          title: "خطأ في البيانات!",
          text: "يرجى المحاولة مرة أخرى أو التواصل مع الدعم الفني.",
          icon: "error",
          button: "حسناً",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getFieldStatus = (fieldName) => {
    if (touched[fieldName] && errors[fieldName]) {
      return 'error';
    }
    if (touched[fieldName] && formData[fieldName] && !errors[fieldName]) {
      return 'success';
    }
    return 'default';
  };



  // دالة جلب بيانات المجموعات من الـ API
  const fetchGroups = async () => {
    setGroupsLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/groups`);
      if (response.data.success) {
        // جلب جميع المجموعات بما فيها Online
        setGroups(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch groups:", error);
    } finally {
      setGroupsLoading(false);
    }
  };

  // فلترة المجموعات حسب السنة الدراسية مع استثناء Online من عرض السنترات
  const displayGroups = groups.filter(group => {
    // استثناء Online من عرض السنترات
    if (group.name === "Online") return false;
    
    // إذا لم يتم اختيار سنة دراسية، لا تعرض أي مجموعات
    if (!formData.level) return false;
    
    // مقارنة level المجموعة مع السنة الدراسية المختارة
    return group.level === parseInt(formData.level);
  });

  return (
    <div className={`stylish-register-page ${style ? "dark-mode" : ""}`}>
      <div className="form-box">
        <div className="form-panel col-lg-8 col-md-12">
          <form onSubmit={handleRegister} noValidate>
            <div className="title-wrapper">
              <h2>
                انشئ <span>حساب</span>
              </h2>
            </div>
            
            <div className="input-row">
              <div className={`input-field ${getFieldStatus('fname1')}`}>
                <input
                  id="fname1"
                  type="text"
                  placeholder=" "
                  value={formData.fname1}
                  name="fname1"
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                />
                <label htmlFor="fname1">الاسم الأول</label>
                {getFieldStatus('fname1') === 'success' && (
                  <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
                )}
                {getFieldStatus('fname1') === 'error' && (
                  <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
                )}
                {errors.fname1 && (
                  <small className="error-text">{errors.fname1}</small>
                )}
              </div>
              
              <div className={`input-field ${getFieldStatus('fname2')}`}>
                <input
                  id="fname2"
                  type="text"
                  placeholder=" "
                  value={formData.fname2}
                  name="fname2"
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                />
                <label htmlFor="fname2">الاسم الثاني</label>
                {getFieldStatus('fname2') === 'success' && (
                  <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
                )}
                {getFieldStatus('fname2') === 'error' && (
                  <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
                )}
                {errors.fname2 && (
                  <small className="error-text">{errors.fname2}</small>
                )}
              </div>
            </div>
            
            <div className="input-row">
              <div className={`input-field ${getFieldStatus('lname1')}`}>
                <input
                  id="lname1"
                  type="text"
                  placeholder=" "
                  value={formData.lname1}
                  name="lname1"
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                />
                <label htmlFor="lname1">الاسم الثالث</label>
                {getFieldStatus('lname1') === 'success' && (
                  <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
                )}
                {getFieldStatus('lname1') === 'error' && (
                  <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
                )}
                {errors.lname1 && (
                  <small className="error-text">{errors.lname1}</small>
                )}
              </div>
              
              <div className={`input-field ${getFieldStatus('lname2')}`}>
                <input
                  id="lname2"
                  type="text"
                  placeholder=" "
                  value={formData.lname2}
                  name="lname2"
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                />
                <label htmlFor="lname2">الاسم الرابع</label>
                {getFieldStatus('lname2') === 'success' && (
                  <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
                )}
                {getFieldStatus('lname2') === 'error' && (
                  <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
                )}
                {errors.lname2 && (
                  <small className="error-text">{errors.lname2}</small>
                )}
              </div>
            </div>
            
            <div className="input-row">
              <div className={`input-field ${getFieldStatus('phone')}`}>
                <input
                  id="phone"
                  type="number"
                  placeholder=" "
                  value={formData.phone}
                  name="phone"
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                />
                <label htmlFor="phone">رقم تليفون الطالب</label>
                {getFieldStatus('phone') === 'success' && (
                  <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
                )}
                {getFieldStatus('phone') === 'error' && (
                  <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
                )}
                {errors.phone && (
                  <small className="error-text">{errors.phone}</small>
                )}
              </div>
              
              <div className={`input-field ${getFieldStatus('pphone')}`}>
                <input
                  id="pphone"
                  type="number"
                  placeholder=" "
                  value={formData.pphone}
                  name="pphone"
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                />
                <label htmlFor="pphone">رقم تليفون ولي الأمر (واتساب)</label>
                {getFieldStatus('pphone') === 'success' && (
                  <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
                )}
                {getFieldStatus('pphone') === 'error' && (
                  <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
                )}
                {errors.pphone && (
                  <small className="error-text">{errors.pphone}</small>
                )}
              </div>
            </div>
            
            <div className={`input-field ${getFieldStatus('email')}`}>
              <input
                id="email"
                type="email"
                placeholder=" "
                value={formData.email}
                name="email"
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
              />
              <label htmlFor="email">البريد الإلكتروني</label>
              {getFieldStatus('email') === 'success' && (
                <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
              )}
              {getFieldStatus('email') === 'error' && (
                <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
              )}
              {errors.email && (
                <small className="error-text">{errors.email}</small>
              )}
            </div>
            
            <div className={`input-field ${getFieldStatus('level')}`}>
              <select
                id="level"
                value={formData.level}
                name="level"
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
              >
                <option value="" disabled>
                  اختر السنة الدراسية
                </option>
                <option value="1">  الصف الثاني الثانوي (بكالوريا) </option>
                <option value="2">  الصف الثالث الثانوي (إحصاء) </option>
                <option value="3">  الصف الثالث الثانوي (رياضة) </option>
              </select>
              {getFieldStatus('level') === 'success' && (
                <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
              )}
              {getFieldStatus('level') === 'error' && (
                <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
              )}
              {errors.level && (
                <small className="error-text">{errors.level}</small>
              )}
            </div>
            <div className={`input-field ${getFieldStatus('student_kind')}`}>
              <select
                id="student_kind"
                value={formData.student_kind}
                name="student_kind"
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
              >
                <option value="" disabled>
                  نوع الدراسة
                </option>
                <option value="عام">  عام </option>
                <option value="أزهر"> أزهر </option>
                {formData.level === "1" && (
                  <option value="بكالوريا"> بكالوريا </option>
                )}
              </select>
              {getFieldStatus('student_kind') === 'success' && (
                <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
              )}
              {getFieldStatus('student_kind') === 'error' && (
                <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
              )}
              {errors.student_kind && (
                <small className="error-text">{errors.student_kind}</small>
              )}
            </div>
            
            {/* نوع الدراسة - يظهر للصف الثاني والثالث الثانوي */}
            {isSecondaryLevel(formData.level) && (
              <div className={`input-field ${getFieldStatus('type')}`}>
                <select
                  id="type"
                  value={formData.type}
                  name="type"
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                >
                  <option value="" disabled>اختر نظام الدراسة</option>
                  <option value="0">سنتر</option>
                  <option value="1">أونلاين</option>
                </select>
                {getFieldStatus('type') === 'success' && (
                  <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
                )}
                {getFieldStatus('type') === 'error' && (
                  <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
                )}
                {errors.type && (
                  <small className="error-text">{errors.type}</small>
                )}
              </div>
            )}
            
            {/* حقول السنتر - تظهر فقط عند اختيار سنتر */}
            {isSecondaryLevel(formData.level) && formData.type === "0" && (
              <>
                <div className={`input-field ${getFieldStatus('center')}`}>
                  <select
                    id="center"
                    value={formData.center}
                    name="center"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                    disabled={groupsLoading}
                  >
                    <option value="" disabled>
                      {groupsLoading ? "جاري تحميل السنترات..." : "اختر السنتر"}
                    </option>
                    {!groupsLoading && displayGroups.map(group => (
                      <option key={group.id} value={group.name}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                  
                  {/* Loading indicator */}
                  {groupsLoading && (
                    <div className="loading-indicator">
                      <div className="spinner-small"></div>
                      <span>جاري تحميل السنترات...</span>
                    </div>
                  )}
                  
                  {getFieldStatus('center') === 'success' && (
                    <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
                  )}
                  {getFieldStatus('center') === 'error' && (
                    <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
                  )}
                  {errors.center && (
                    <small className="error-text">{errors.center}</small>
                  )}
                </div>
                
                <div className={`input-field ${getFieldStatus('center_id')}`}>
                  <input
                    id="center_id"
                    type="number"
                    placeholder=" "
                    value={formData.center_id}
                    name="center_id"
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                  />
                  <label htmlFor="center_id">    أدخل Id المستر   </label>
                  {getFieldStatus('center_id') === 'success' && (
                    <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
                  )}
                  {getFieldStatus('center_id') === 'error' && (
                    <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
                  )}
                  {errors.center_id && (
                    <small className="error-text">{errors.center_id}</small>
                  )}
                </div>
              </>
            )}

            <div className={`input-field ${getFieldStatus('government')}`}>
              <select
                id="government"
                value={formData.government}
                name="government"
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
              >
                <option value="" disabled>اختر المحافظة</option>
                <option value="القاهرة">القاهرة</option>
                <option value="الجيزة">الجيزة</option>
                <option value="الإسكندرية">الإسكندرية</option>
                <option value="الإسماعيلية">الإسماعيلية</option>
                <option value="كفر الشيخ">كفر الشيخ</option>
                <option value="أسوان">أسوان</option>
                <option value="أسيوط">أسيوط</option>
                <option value="الأقصر">الأقصر</option>
                <option value="الوادي الجديد">الوادي الجديد</option>
                <option value="شمال سيناء">شمال سيناء</option>
                <option value="البحيرة">البحيرة</option>
                <option value="بني سويف">بني سويف</option>
                <option value="بورسعيد">بورسعيد</option>
                <option value="البحر الأحمر">البحر الأحمر</option>
                <option value="الدقهلية">الدقهلية</option>
                <option value="جنوب سيناء">جنوب سيناء</option>
                <option value="دمياط">دمياط</option>
                <option value="سوهاج">سوهاج</option>
                <option value="السويس">السويس</option>
                <option value="الشرقية">الشرقية</option>
                <option value="الغربية">الغربية</option>
                <option value="الفيوم">الفيوم</option>
                <option value="القليوبية">القليوبية</option>
                <option value="قنا">قنا</option>
                <option value="مطروح">مطروح</option>
                <option value="المنوفية">المنوفية</option>
                <option value="المنيا">المنيا</option>
              </select>
              {getFieldStatus('government') === 'success' && (
                <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
              )}
              {getFieldStatus('government') === 'error' && (
                <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
              )}
              {errors.government && (
                <small className="error-text">{errors.government}</small>
              )}
            </div>

            <div className={`input-field file-field ${getFieldStatus('image')}`}>
              <label htmlFor="image" className="file-label">صورة الطالب</label>
              <input
                id="image"
                type="file"
                name="image"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="image-preview-wrap">
                  <img src={imagePreview} alt="معاينة صورة الطالب" className="image-preview" />
                </div>
              )}
              {getFieldStatus('image') === 'success' && (
                <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
              )}
              {getFieldStatus('image') === 'error' && (
                <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
              )}
              {errors.image && (
                <small className="error-text">{Array.isArray(errors.image) ? errors.image[0] : errors.image}</small>
              )}
            </div>

            
            <div className="input-row">
              <div className={`input-field password-field ${getFieldStatus('password')}`}>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder=" "
                  value={formData.password}
                  name="password"
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                />
                <label htmlFor="password">كلمة المرور</label>
                <span
                  className="password-toggle-icon"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </span>
                {getFieldStatus('password') === 'success' && (
                  <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
                )}
                {getFieldStatus('password') === 'error' && (
                  <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
                )}
                {errors.password && (
                  <small className="error-text">{errors.password}</small>
                )}
              </div>
              
              <div className={`input-field password-field ${getFieldStatus('password_confirmation')}`}>
                <input
                  id="password_confirmation"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder=" "
                  value={formData.password_confirmation}
                  name="password_confirmation"
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                />
                <label htmlFor="password_confirmation">تأكيد كلمة المرور</label>
                <span
                  className="password-toggle-icon"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                </span>
                {getFieldStatus('password_confirmation') === 'success' && (
                  <FontAwesomeIcon icon={faCheckCircle} className="field-status-icon success" />
                )}
                {getFieldStatus('password_confirmation') === 'error' && (
                  <FontAwesomeIcon icon={faExclamationTriangle} className="field-status-icon error" />
                )}
                {errors.password_confirmation && (
                  <small className="error-text">{errors.password_confirmation}</small>
                )}
              </div>
            </div>
            
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner"></span>
                  <span>جاري الإنشاء...</span>
                </>
              ) : (
                "انشئ الحساب"
              )}
            </button>
          </form>
        </div>
        
 
        <div className="welcome-panel col-lg-4 col-md-12">
          <div className="welcome-content">
            <h2>   عايز تبقي مع الدولة ؟   </h2>
            <p>
              لو عند حساب بالفعل سجل دخولك الآن وابدأ رحلتك معانا .
            </p>
            <Link to="/loginuser" className="welcome-link">
              لدي حساب بالفعل!
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Registeruser;




 

